import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { Building2, Palette } from 'lucide-react';
import { Card, PageHeader } from '@/components/ui/Card';
import { qk, fetchClients } from '@/lib/queries';

/** Brand kits (Aziz 2026-10-25): everyone on a clinic reaches its brand kit, tasks-level
 *  people included, who have no Clients page. The list is the clinics the person is on
 *  (GET /clients already scopes it); one clinic goes straight in. */
export default function BrandKitsPage() {
  const { data, isLoading } = useQuery({ queryKey: qk.clients, queryFn: fetchClients });

  if (isLoading) return <div className="text-muted text-sm">Loading...</div>;
  const list = data ?? [];
  if (list.length === 1) return <Navigate to={`/clients/${list[0].id}/brand-kit`} replace />;

  return (
    <div>
      <PageHeader title="Brand kits" subtitle="The brand, templates and generator of each clinic you work on" />
      {list.length === 0 ? (
        <Card>
          <div className="text-center py-8 space-y-2">
            <Palette className="mx-auto text-muted" size={36} />
            <p className="text-muted text-sm">You are not on any clinic yet.</p>
            <p className="text-xs text-muted/70">A manager adds you to a clinic in Admin.</p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((c) => (
            <Link key={c.id} to={`/clients/${c.id}/brand-kit`} data-testid="brand-kit-clinic">
              <Card className="hover:border-accent-400/50 transition cursor-pointer h-full">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-600/20 flex items-center justify-center text-accent-400">
                    {c.logo_url
                      ? <img src={c.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      : <Building2 size={18} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-muted-strong truncate">{c.name}</div>
                    <div className="text-xs text-muted capitalize">{c.vertical.replace('_', ' ')}</div>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
